import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import Icon from "@mdi/react";
import { mdiHome, mdiDoctor, mdiAccountHeart, mdiChartLine, mdiStethoscope, mdiVideo } from "@mdi/js";
import styles from "./styles.module.scss";
import { IconSidebar } from "./components/IconSidebar";
import { clsx } from "@ismael1361/utils";
import { Link } from "components";

export { ListSidebar } from "./components/ListSidebar";

// ── Mobile Layout Context ─────────────────────────────────────────

interface MobileLayoutContextType {
	isMobile: boolean;
	isSidebarVisible: boolean;
	showContent: () => void;
	showSidebar: () => void;
	openDrawer: () => void;
	closeDrawer: () => void;
}

const MobileLayoutContext = createContext<MobileLayoutContextType>({
	isMobile: false,
	isSidebarVisible: true,
	showContent: () => {},
	showSidebar: () => {},
	openDrawer: () => {},
	closeDrawer: () => {},
});

export const useMobileLayout = () => useContext(MobileLayoutContext);

// ── useIsMobile hook ──────────────────────────────────────────────

const useIsMobile = (breakpoint = 768) => {
	const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= breakpoint : false));
	useEffect(() => {
		const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
		const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
		setIsMobile(mq.matches);
		mq.addEventListener("change", handler);
		return () => mq.removeEventListener("change", handler);
	}, [breakpoint]);
	return isMobile;
};

// ── Mobile Layout Provider ────────────────────────────────────────
// Must wrap page components so they can access the context via useMobileLayout()

export const MobileLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const isMobile = useIsMobile();
	const [mobileSidebarVisible, setMobileSidebarVisible] = useState(true);
	const [drawerOpen, setDrawerOpen] = useState(false);

	// Reset sidebar visibility when switching between mobile/desktop
	useEffect(() => {
		if (!isMobile) {
			setMobileSidebarVisible(true);
			setDrawerOpen(false);
		}
	}, [isMobile]);

	const mobileContext = useMemo(
		() => ({
			isMobile,
			isSidebarVisible: mobileSidebarVisible,
			showContent: () => setMobileSidebarVisible(false),
			showSidebar: () => setMobileSidebarVisible(true),
			openDrawer: () => setDrawerOpen(true),
			closeDrawer: () => setDrawerOpen(false),
		}),
		[isMobile, mobileSidebarVisible],
	);

	return (
		<MobileLayoutContext.Provider value={mobileContext}>
			{children}
			{isMobile && <MobileNavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />}
		</MobileLayoutContext.Provider>
	);
};

// ── Mobile Navigation Drawer ──────────────────────────────────────

interface DrawerNavItem {
	icon: string;
	activeIcon: string;
	label: string;
	href: string;
	isActive: () => boolean;
}

const drawerNavItems: DrawerNavItem[] = [
	{
		icon: mdiHome,
		activeIcon: mdiHome,
		label: "Início",
		href: "/medical",
		isActive: () => window.location.pathname === "/medical" || window.location.pathname === "/",
	},
	// ── Medical ────────────────────────────────
	{
		icon: mdiDoctor,
		activeIcon: mdiDoctor,
		label: "Médico",
		href: "/medical/doctor",
		isActive: () => /^\/medical\/doctor/i.test(window.location.pathname),
	},
	{
		icon: mdiAccountHeart,
		activeIcon: mdiAccountHeart,
		label: "Paciente",
		href: "/medical/patient",
		isActive: () => /^\/medical\/patient/i.test(window.location.pathname),
	},
	{
		icon: mdiChartLine,
		activeIcon: mdiChartLine,
		label: "Dashboard",
		href: "/medical/dashboard",
		isActive: () => /^\/medical\/dashboard/i.test(window.location.pathname),
	},
	{
		icon: mdiVideo,
		activeIcon: mdiVideo,
		label: "Teleconsulta",
		href: "/medical",
		isActive: () => /^\/medical\/teleconsultation/i.test(window.location.pathname),
	},
];

const MobileNavDrawer: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
	const [, setTick] = useState(0);

	useEffect(() => {
		const handler = () => setTick((t) => t + 1);
		window.addEventListener("popstate", handler);
		return () => window.removeEventListener("popstate", handler);
	}, []);

	// Close drawer on navigation
	const handleNav = useCallback(() => {
		onClose();
	}, [onClose]);

	// Close on Escape
	useEffect(() => {
		if (!open) return;
		const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [open, onClose]);

	return (
		<>
			{/* Backdrop */}
			<div
				className={clsx(styles.drawerBackdrop, open && styles.drawerBackdropVisible)}
				onClick={onClose}
			/>

			{/* Drawer panel */}
			<nav className={clsx(styles.drawerPanel, open && styles.drawerPanelOpen)}>
				<div className={styles.drawerHeader}>
					<span className={styles.drawerLogo}>Med.IA</span>
				</div>
				<div className={styles.drawerItems}>
					{drawerNavItems.map((item, i) => {
						const active = item.isActive();
						return (
							<Link
								key={i}
								href={item.href}
								className={clsx(styles.drawerItem, active && styles.drawerItemActive)}
								onClick={handleNav}
							>
								<Icon path={active ? item.activeIcon : item.icon} size={1} />
								<span>{item.label}</span>
							</Link>
						);
					})}
				</div>
			</nav>
		</>
	);
};

// ── Main Layout ───────────────────────────────────────────────────

interface MainLayoutProps {
	children?: React.ReactNode;
	sidebar?: React.ReactNode;
	style?: React.CSSProperties;
	contentStyle?: React.CSSProperties;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children, sidebar, style, contentStyle }) => {
	const { isMobile, isSidebarVisible } = useMobileLayout();
	const hasSidebar = !!sidebar;
	const effectiveSidebarVisible = hasSidebar ? isSidebarVisible : false;

	return (
		<div className={clsx(styles.layout, isMobile && styles.mobileLayout)} style={style}>
			<IconSidebar />
			<main className={styles.mainContent} style={contentStyle}>
				{sidebar && (
					<aside
						className={clsx(
							styles.listSidebar,
							isMobile && styles.mobileFullWidth,
							isMobile && !effectiveSidebarVisible && styles.mobileHidden,
						)}
					>
						{sidebar}
					</aside>
				)}
				<div
					className={clsx(styles.content, isMobile && hasSidebar && effectiveSidebarVisible && styles.mobileHidden)}
				>
					{children ? (
						children
					) : (
						<div className={styles.emptyState}>
							<span className={styles.blurText}>Sem conteúdo</span>
						</div>
					)}
				</div>
			</main>
		</div>
	);
};
